import { Context } from "@apollo/client";
import Action from "csi/def/Action";
import { Field } from "csi/def/Field";
import Relationship from "csi/def/Relationship";
import ViewRef from "csi/def/ViewRef";
import enums from "csi/enums";
import FormWidget from "csi/form/Widget";
import html from "csi/html";
import locale from "csi/locale";
import reflection from "csi/reflection";
import { resolveNode } from "csi/service/nodeService";
import { C8Node } from "csi/typings";
import xml from "csi/xml";
import Query from "csi/xml/Query";
import Grid from "dgrid/Grid";
import aspect from "dojo/aspect";
import domClass from "dojo/dom-class";
import declare from "dojo/_base/declare";
import lang from "dojo/_base/lang";
import _ from "lodash";


// Rules for field hidding
const EXP1 = "validValue('PasteData')";
const EXP2 = "!" + EXP1;

const DataSheetItemsPasteForm = declare(FormWidget, {
	// Fields' ID
	CONFIG: "PasteConfiguration",
	DATA: "PasteData",
	MODE: "PasteMode",
	START_ROW: "PasteStartRow",

	// Boolean: write only visible fields
	bWriteVisible: false,

	/** Cache parsed data rows to add to server call */
	_dataRows: null,

	/** Original unparsed paste data */
	_dataOriginal: null,

	/** Delimiter for spliting columns */
	_columnDelimiter: null,

	/** Node type of the paste configuration. Example, "RecipeItemsImportConfiguration" */
	_configNodeType: "",

	/** View Id of the Paste Configurations, example: "RecipeItemsImportConfigurations" */
	_configViewId: "",

	/** Node type of the line item to be pasted. Example, "RecipeLineItem" */
	_lineItemNodeType: "",

	/** Object: map of attributes to expressions indicating if fields should be hidden */
	_hideExpressions: {
		PasteData: EXP1,
		PasteMode: EXP2,
		PasteStartRow: EXP2,
	},

	/**
	 * Through the import configuration, get the column numbers (user input integer starting from 1) which will be used on the Paste action
	 */
	_getUsedColumnNumbers(config: C8Node): Array<number> {
		// Note that the user input column number start from 1 (instead of TypeScript number which start from 0)
		let columnsUsed = [];

		columnsUsed = _.map(config.ItemUniqueIDColumnMap).filter((value: number) => value > 0);

		const attColumnMap = _.map(config.AttributeColumnMap);
		for (let i = 0; i < attColumnMap.length; i++) {
			const curColumn = attColumnMap[i] as number;
			if (curColumn > 0) {
				columnsUsed.push(curColumn);
			}
		}
		return columnsUsed;
	},

	/**
	 * Get the column numbers (user input integer starting from 1) which will be used on for numeric attributes
	 */
	_getNumericAttributeColumnNumbers(config: C8Node): Array<number> {
		const columnNumers = [];

		const rc = reflection.getClass(this._lineItemNodeType);
		if (!rc) {
			return columnNumers;
		}

		const attrColumnMap = config.AttributeColumnMap;
		rc.getAttributes().forEach((reflectionAttr) => {
			const attType = reflectionAttr.getType();
			if (xml.attributeTypeIsNumeric(attType)) {
				const key = reflectionAttr.getId();
				const curColumn = attrColumnMap[key];
				if (curColumn) {
					columnNumers.push(curColumn);
				}
			}
		});

		return columnNumers;
	},

	/**
	 * Grey out columns not to be imported (C8-40228)
	 */
	_greyOutUnusedColumns(columnsUsed: Array<number>, grid: Grid, columns: Array<object>) {
		const columnDictionary = _.keyBy(columnsUsed, (column) => column);
		for (let i = 0; i < columns.length; i++) {
			if (!columnDictionary[i + 1]) {
				grid.styleColumn(i, "color: var(--Black500); background-color: var(--Grey100); border-color: var(--Grey400);");
			}
		}
	},

	_isNumeric(str: any) {
		return !isNaN(str) && !isNaN(parseFloat(str));
	},

	/**
	 * Parse data to see if it is valid - if so, then convert to HTML table and display other fields
	 * @param {ClipboardEvent} e - paste event
	 */
	async _onWatchData(e:ClipboardEvent) {
		const newValue = e.clipboardData.getData("text");
		this._dataOriginal = newValue;
		const config = await this._getConfiguration();
		await this._parseDataAndUpdatePreview(this._dataOriginal, config);
	},

	/**
	 * Parse the paste data and diaplay tabulated preview of the data
	 * @param {string} rawPasteData - Unparsed data user input
	 * @param {C8Node} config - Import configuration node
	 */
	_parseDataAndUpdatePreview(rawPasteData: string, config: C8Node): void {
		if (!rawPasteData || !config) {
			return;
		}

		try {
			const startRow = this.getFieldValue(this.START_ROW);
			if (startRow < 1) {
				throw new Error(locale.resource("PasteStartRowError"));
			}

			this._dataRows = this._parseData(rawPasteData, config);

			const cols = this._dataRows[0];

			// render HTML to show data
			const columns = _.map(cols, (col, i) => { return { label: col, field: i, sortable: false }; });
			const commentNode = this.bodyNode.firstChild.firstChild;

			html.clear(commentNode);

			const grid = new Grid({
				class: "formPasteGrid",
				showHeader: false,
				columns: columns,
			}, html.create("div", null, commentNode));

			const columnsUsed = this._getUsedColumnNumbers(config);
			this._greyOutUnusedColumns(columnsUsed, grid, columns);

			//C8-40260: grey out rows not to imported
			if (startRow > 1) {
				let iRow = 1;
				this.own(aspect.after(grid, "renderRow", (row: HTMLDivElement, args) => {
					if (iRow < startRow) {
						domClass.add(row, "dgrid-row-unused");
					}
					iRow++;
					return row;
				}));
			}

			grid.renderArray(this._dataRows);

			// C8-40947: Warning on paste data of empty / invalid value
			let cntEmptyCells = 0;
			let cntMismatchCells = 0;

			const cntColumns = cols.length;
			const usedColumnDictionary = _.keyBy(columnsUsed, (column) => column);

			const columnsNumeric = this._getNumericAttributeColumnNumbers(config);
			const numbericColumnDictionary = _.keyBy(columnsNumeric, (column) => column);

			// loop thru rows of paste data
			for (let iRow = startRow - 1; iRow < this._dataRows.length; iRow++) {
				const curRow = this._dataRows[iRow];
				//loop thru the column on current row of paste data and check (1) if cell is empty; (2) numeric attribute cell has numeric value
				for (let iCol = 0; iCol < cntColumns; iCol++) {
					if (!usedColumnDictionary[iCol + 1]) {
						// skip unused column
						continue;
					}

					const cellText = curRow[iCol];
					if (!cellText || !cellText.length) {
						cntEmptyCells++;
					} else if (numbericColumnDictionary[iCol + 1] && !this._isNumeric(cellText)) {
						cntMismatchCells++;
					}
				}
			}

			let warnMsg = "";
			if (cntEmptyCells || cntMismatchCells) {
				warnMsg = locale.resource("PasteDataWarning") + "\n";
			}
			if (cntEmptyCells == 1) {
				warnMsg += locale.resource("PasteDataEmptyCellWarning", [cntEmptyCells]);
			} else if (cntEmptyCells > 1) {
				warnMsg += locale.resource("PasteDataEmptyCellsWarning", [cntEmptyCells]);
			}

			if (cntMismatchCells == 1) {
				warnMsg += cntEmptyCells ? " and " : "";
				warnMsg += locale.resource("PasteDataNonNumericCellWarning", [cntMismatchCells]);
			} else if (cntMismatchCells > 1) {
				warnMsg += cntEmptyCells ? " and " : "";
				warnMsg += locale.resource("PasteDataNonNumericCellsWarning", [cntMismatchCells]);
			}

			this.invalidNode.set("icon", this.invalidNode.WARNING);
			this.invalidNode.set("value", warnMsg);

			// set the field value to trigger change to visibility of other fields
			this.setFieldValue(this.DATA, rawPasteData);
		} catch (ex) {
			// show error for given input
			this.invalidNode.set("icon", this.invalidNode.ERROR);
			this.invalidNode.set("value", ex.message);
		}
	},

	/**
	 * Parse entered data
	 * @param {string} data
	 * @param {C8Node} config - Import configuration node
	 * @returns
	 */
	_parseData(data: string, config: C8Node) {
		// parse data into rows, skipping empty rows
		const rows = data.split("\n").filter((s) => !!s.trim());

		// parse rows into columns, first compressing multiple blanks into single blanks
		const delimiter = this._columnDelimiter = this._columnDelimiter || this._getDelimiter(config);
		return _.map(rows, (row) => {
			return row.replace(/[ ]+/g, " ").split(delimiter);
		});
	},

	async _getConfiguration() {
		const urlConfig = this.defView.getFieldValue(this.CONFIG);
		return await resolveNode(urlConfig, this.cacheView);
	},

	_getDelimiter(config: C8Node) {
		if (config) {
			const configDelimiter = config.Delimiter;
			if (configDelimiter == "ImportDelimiter:Tab") {
				return "\t";
			} else if (configDelimiter == "ImportDelimiter:Comma") {
				return ",";
			}
		}

		return "\t";
	},

	/**
	 * Called when selected Paste Configuration changed
	 */
	async _onChangeConfiguration(defView, field, value) {
		const config = await this._getConfiguration();
		this._columnDelimiter = this._getDelimiter(config);
		if (config) {
			this.setFieldValue(this.START_ROW, config.StartRow);
		}

		this._parseDataAndUpdatePreview(this._dataOriginal, config);
	},

	async _onChangeStartRow(defView, field, value) {
		const config = await this._getConfiguration();
		this._parseDataAndUpdatePreview(this._dataOriginal, config);
	},

	/**
	 * @override
	 * Only allow Ctrl+V to paste data and watch for when it happens
	 */
	_postView() {
		const w = this.defView.getFieldControl(this.DATA);
		w.onInput = (evt): boolean => {
			return html.ctrlKeyPressed(evt) && (!evt.charOrCode || (evt.charOrCode == "V") || (evt.charOrCode == "v"));
		};
		w.on("paste", lang.hitch(this, this._onWatchData));

		this.inherited(arguments);
	},

	/**
	 * Pack the parsed data back into a string that's easy for the server to parse
	 */
	_reassembleData(rows: Array<Array<string>>) {
		const delimiter = this._columnDelimiter;
		return _.map(rows, (row) => {
			return row.join(delimiter);
		}).join("\n");
	},

	/**
	 * @override
	 */
	_resolveView() {
		const fieldConfig = this.defView.getField(this.CONFIG);
		if (fieldConfig) {
			fieldConfig.m_onChange = "_onChangeConfiguration";
		}

		const fieldStartRow = this.defView.getField(this.START_ROW);
		if (fieldStartRow) {
			fieldStartRow.m_onChange = "_onChangeStartRow";
		}

		this.addDefaultValue(this.MODE, "ImportOperationMode:Add");

		this.inherited(arguments);
	},

	/**
	 * @override
	 */
	getFieldStore(field, value) {
		field = this.getField(field);
		switch (field.getControlName()) {
			case this.CONFIG:
				return this.getConfigurationStore(field);
			case this.MODE:
				return enums.getStore("ImportOperationMode", value, null, true);
			default:
				return this.inherited(arguments);
		}
	},

	/**
	 * Get store for the Configuration field
	 */
	getConfigurationStore(field: Field) {
		const q = new Query().Attr("Active", xml.OP_EQUALS, true);

		// create relationship for Configuration dropdown
		const rel = lang.mixin(new Relationship(), {
			m_typeNode: this._configNodeType,
			m_query: q,
		});

		const ctx = this.getContext();
		const store = ctx.view.createHeadingNamesStore(ctx, field, null, rel);

		// create view for goto dialog
		store.action = lang.mixin(new Action(), {
			m_fixedQuery: q,
			m_viewRef: lang.mixin(new ViewRef(), {
				m_id: this._configViewId,
				m_url: reflection.URL_HOME,
			}),
		});

		return store;
	},

	/**
	 * @override
	 * Add attributes to support _hideExpressions
	 */
	getFormContext() {
		const ctx = this.inherited<Context>(arguments);
		const node = ctx.getNode();
		xml.nodeCreateClientAttr(node, this.DATA, xml.ATTR_STRING, this.defView.getFieldValue(this.DATA));
		return ctx;
	},

	/**
	 * @override
	 * Make Data value available to action controller
	 */
	validateForm(errorParams) {
		const val = this.inherited(arguments);
		this.defView.m_formData.setFieldInitialValue("Data", this._reassembleData(this._dataRows));
		return val;
	},

});

export = DataSheetItemsPasteForm;